import React, { useEffect, useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import { staggerContainer, itemVariants } from './dashboardMotion';
import { auth, database } from '../firebaseConfig';
import { ref, onValue, update, remove, push, serverTimestamp } from 'firebase/database';
import { COMMISSION_ROLES, COMMISSION_ROLE_IDS, commissionRoleForMember, memberCommissionMinimum } from './paymentEngine';
import './TeamSettings.css';

const TEAM_PATH = 'salesgod-research/team';

// Erzeugt/liefert den Secret Key eines Mitglieds fuer seine teilbare
// Auszahlungsseite. Der Key liegt unter salesgod-research/teamSecrets und ist
// aus dem Browser nicht lesbar – deshalb laeuft alles ueber die Cloud Function.
const SHARE_FN_URL =
  process.env.REACT_APP_PAYOUT_SHARE_FN_URL ||
  'https://us-central1-bc-landingpage.cloudfunctions.net/setPayoutShare';

// Feste Positionen/Rollen fuer Team-Mitglieder. Werden u. a. genutzt, um pro
// Projekt automatisch den richtigen Outreach-Manager / Team-Copywriter zu finden.
export const TEAM_ROLES = ['Sales-Junior', 'Sales-Senior', 'Outreach-Manager', 'Teamleiter', 'Team-Copywriter'];

// Auswahl fuer die Provisionsstaffel. '' = automatisch aus der Position ableiten,
// 'none' = bewusst keine Staffel (Mitglied bleibt bei Fix/Prozent + Pauschalbonus).
const COMMISSION_ROLE_OPTIONS = [
  { value: '', label: 'Automatisch aus Position' },
  ...COMMISSION_ROLE_IDS.map((id) => ({ value: id, label: COMMISSION_ROLES[id].label })),
  { value: 'none', label: 'Keine Staffel' },
];

// Mitarbeiter-Datensatz -> Formular-Entwurf (Strings, damit Inputs sauber sind).
const toDraft = (m = {}) => ({
  name: m.name || '',
  role: m.role || '',
  email: m.email || '',
  payType: m.payType === 'fixed' ? 'fixed' : 'commission',
  fixedAmount: typeof m.fixedAmount === 'number' ? String(m.fixedAmount) : '',
  commissionPercent: typeof m.commissionPercent === 'number' ? String(m.commissionPercent) : '',
  appointmentBonus: typeof m.appointmentBonus === 'number' ? String(m.appointmentBonus) : '',
  durationMonths: typeof m.durationMonths === 'number' ? String(m.durationMonths) : '',
  commissionRole: typeof m.commissionRole === 'string' ? m.commissionRole : '',
  commissionMin: typeof m.commissionMin === 'number' ? String(m.commissionMin) : '',
  active: m.active !== false,
});

const emptyDraft = () => ({
  name: '', role: '', email: '', payType: 'commission',
  fixedAmount: '', commissionPercent: '', appointmentBonus: '', durationMonths: '',
  commissionRole: '', commissionMin: '', active: true,
});

const num = (s) => {
  const n = Number(String(s).replace(/[^\d.,-]/g, '').replace(/\./g, '').replace(',', '.'));
  return isNaN(n) ? 0 : n;
};

const fmtEur = (n) =>
  !isFinite(n) ? '–' : new Intl.NumberFormat('de-DE', { maximumFractionDigits: 0 }).format(Math.round(n)) + ' €';

const draftToPayload = (d) => {
  const payType = d.payType === 'fixed' ? 'fixed' : 'commission';
  const commissionRole = d.commissionRole === 'none' || COMMISSION_ROLES[d.commissionRole] ? d.commissionRole : '';
  return {
    name: d.name.trim().slice(0, 120),
    role: d.role.trim().slice(0, 120),
    email: d.email.trim().slice(0, 200),
    payType,
    fixedAmount: payType === 'fixed' ? Math.max(0, Math.round(num(d.fixedAmount))) : 0,
    commissionPercent: payType === 'commission' ? Math.max(0, Math.min(100, num(d.commissionPercent))) : 0,
    appointmentBonus: Math.max(0, Math.round(num(d.appointmentBonus))),
    durationMonths: Math.max(0, Math.round(num(d.durationMonths))),
    commissionRole,
    // Leer lassen = Standardminimum der Staffel greift (siehe commissionCore).
    commissionMin: String(d.commissionMin).trim() === '' ? null : Math.max(0, Math.round(num(d.commissionMin))),
    active: d.active !== false,
    updatedAt: serverTimestamp(),
  };
};

// Welche Staffel greift fuer diesen Entwurf – inklusive der automatischen
// Ableitung aus der Position, damit die Vorschau nicht luegt.
const draftCommissionRole = (d) =>
  commissionRoleForMember({ role: d.role, commissionRole: d.commissionRole });

const TeamSettings = () => {
  const [members, setMembers] = useState({});
  const [drafts, setDrafts] = useState({});
  const [newDraft, setNewDraft] = useState(emptyDraft());
  const [savingId, setSavingId] = useState(null);
  const [loading, setLoading] = useState(true);
  // memberId -> { shareUrl, password } bzw. { error }; erst nach dem Erzeugen
  // bzw. Anzeigen gefüllt – der Key wird nie ungefragt geladen.
  const [shares, setShares] = useState({});
  const [shareBusy, setShareBusy] = useState(null);
  const [copiedId, setCopiedId] = useState(null);

  useEffect(() => {
    const unsub = onValue(ref(database, TEAM_PATH), (snap) => {
      const val = snap.val() || {};
      setMembers(val);
      setDrafts((prev) => {
        const next = {};
        Object.keys(val).forEach((id) => {
          // Bestehenden Entwurf behalten (Tippen nicht überschreiben), neue ergänzen.
          next[id] = prev[id] || toDraft(val[id]);
        });
        return next;
      });
      setLoading(false);
    });
    return () => unsub();
  }, []);

  const setField = (id, field, value) =>
    setDrafts((prev) => ({ ...prev, [id]: { ...prev[id], [field]: value } }));

  const isDirty = (id) =>
    members[id] && JSON.stringify(toDraft(members[id])) !== JSON.stringify(drafts[id]);

  const saveMember = async (id) => {
    const d = drafts[id];
    if (!d || !d.name.trim()) return;
    setSavingId(id);
    try {
      await update(ref(database, `${TEAM_PATH}/${id}`), draftToPayload(d));
    } catch (e) {
      console.error('Mitarbeiter speichern fehlgeschlagen:', e);
    } finally {
      setSavingId(null);
    }
  };

  const resetMember = (id) =>
    setDrafts((prev) => ({ ...prev, [id]: toDraft(members[id]) }));

  const deleteMember = async (id) => {
    const name = members[id]?.name || 'diesen Mitarbeiter';
    if (!window.confirm(`„${name}" wirklich löschen?`)) return;
    try {
      await remove(ref(database, `${TEAM_PATH}/${id}`));
      setDrafts((prev) => { const n = { ...prev }; delete n[id]; return n; });
    } catch (e) {
      console.error('Mitarbeiter löschen fehlgeschlagen:', e);
    }
  };

  const addMember = async () => {
    if (!newDraft.name.trim()) return;
    try {
      const node = push(ref(database, TEAM_PATH));
      await update(node, { ...draftToPayload(newDraft), createdAt: serverTimestamp() });
      setNewDraft(emptyDraft());
    } catch (e) {
      console.error('Mitarbeiter anlegen fehlgeschlagen:', e);
    }
  };

  const ids = useMemo(
    () => Object.keys(members).sort((a, b) => (members[a]?.name || '').localeCompare(members[b]?.name || '')),
    [members]
  );

  // Auszahlungs-Link holen (bzw. beim ersten Mal erzeugen). `regenerate` setzt
  // einen neuen Key – alte Links werden damit sofort ungültig.
  const loadShare = async (id, regenerate = false) => {
    if (regenerate && !window.confirm('Neuen Key erzeugen? Der bisherige Link funktioniert danach nicht mehr.')) return;
    setShareBusy(id);
    try {
      const token = auth.currentUser ? await auth.currentUser.getIdToken() : null;
      const resp = await fetch(SHARE_FN_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        body: JSON.stringify({ memberId: id, regenerate }),
      });
      const data = await resp.json().catch(() => ({}));
      if (resp.status === 200 && data.ok) {
        setShares((prev) => ({ ...prev, [id]: { shareUrl: data.shareUrl, url: data.url, password: data.password } }));
      } else {
        setShares((prev) => ({ ...prev, [id]: { error: data.error || 'Link konnte nicht erzeugt werden.' } }));
      }
    } catch (e) {
      setShares((prev) => ({ ...prev, [id]: { error: 'Netzwerkfehler: ' + e.message } }));
    } finally {
      setShareBusy(null);
    }
  };

  const copyShare = async (id) => {
    const share = shares[id];
    if (!share || !share.shareUrl) return;
    try {
      await navigator.clipboard.writeText(share.shareUrl);
      setCopiedId(id);
      setTimeout(() => setCopiedId((cur) => (cur === id ? null : cur)), 1800);
    } catch (e) {
      window.prompt('Link kopieren:', share.shareUrl);
    }
  };

  const renderShare = (id) => {
    const share = shares[id];
    const busy = shareBusy === id;
    return (
      <div className="ts-share">
        <div className="ts-share-text">
          <span className="ts-share-title">Auszahlungs-Link</span>
          <span className="ts-share-hint">
            Persönliche Seite mit allen fälligen, voraussichtlichen und bereits ausgezahlten Beträgen –
            teilbar, nur mit Secret Key aufrufbar.
          </span>
        </div>
        {share && share.shareUrl ? (
          <div className="ts-share-result">
            <code className="ts-share-url">{share.shareUrl}</code>
            <div className="ts-share-actions">
              <span className="ts-share-key">Key: {share.password}</span>
              <button type="button" className="ts-btn ts-btn-primary" onClick={() => copyShare(id)}>
                {copiedId === id ? 'Kopiert ✓' : 'Link kopieren'}
              </button>
              <button type="button" className="ts-btn ts-btn-ghost" onClick={() => loadShare(id, true)} disabled={busy}>
                Neuer Key
              </button>
            </div>
          </div>
        ) : (
          <div className="ts-share-actions">
            {share && share.error && <span className="ts-share-err">{share.error}</span>}
            <button type="button" className="ts-btn ts-btn-ghost" onClick={() => loadShare(id)} disabled={busy}>
              {busy ? 'Moment …' : 'Link anzeigen'}
            </button>
          </div>
        )}
      </div>
    );
  };

  const renderPayoutPreview = (d) => {
    const months = num(d.durationMonths);
    const duration = months > 0 ? `${months} Monate lang` : 'laufend (unbegrenzt)';
    const roleId = draftCommissionRole(d);
    const planNote = roleId
      ? `+ ${COMMISSION_ROLES[roleId].label}-Staffel · min. ${fmtEur(memberCommissionMinimum({ commissionMin: String(d.commissionMin).trim() === '' ? undefined : num(d.commissionMin) }, roleId))}`
      : null;
    if (d.payType === 'fixed') {
      const pay = num(d.fixedAmount);
      const total = months > 0 ? pay * months : null;
      return (
        <div className="ts-preview">
          <div className="ts-preview-line">
            <span className="ts-preview-val">{fmtEur(pay)}</span>
            <span className="ts-preview-lbl">/ Monat</span>
          </div>
          <div className="ts-preview-meta">
            <span>{duration}</span>
            {total != null && <span>· gesamt {fmtEur(total)}</span>}
            {planNote && <span className="ts-preview-plan">{planNote}</span>}
          </div>
        </div>
      );
    }
    return (
      <div className="ts-preview">
        <div className="ts-preview-line">
          <span className="ts-preview-val">{num(d.commissionPercent)}%</span>
          <span className="ts-preview-lbl">pro Kunde / Monat</span>
        </div>
        <div className="ts-preview-meta">
          <span>{duration}</span>
          {planNote && <span className="ts-preview-plan">{planNote}</span>}
        </div>
      </div>
    );
  };

  const renderFields = (d, onChange) => (
    <>
      <div className="ts-row">
        <label className="ts-field ts-field-grow">
          <span>Name</span>
          <input type="text" value={d.name} placeholder="Mitarbeiter-Name" onChange={(e) => onChange('name', e.target.value)} />
        </label>
        <label className="ts-field">
          <span>Position</span>
          <select className="ts-select" value={d.role} onChange={(e) => onChange('role', e.target.value)}>
            <option value="">— wählen —</option>
            {TEAM_ROLES.map((r) => (
              <option key={r} value={r}>{r}</option>
            ))}
            {d.role && !TEAM_ROLES.includes(d.role) && <option value={d.role}>{d.role}</option>}
          </select>
        </label>
      </div>

      <div className="ts-row">
        <label className="ts-field ts-field-grow">
          <span>E-Mail (optional)</span>
          <input type="email" value={d.email} placeholder="name@firma.com" onChange={(e) => onChange('email', e.target.value)} />
          <small className="ts-field-hint">Wird automatisch als CC gesetzt, wenn diese:r Mitarbeiter:in einem Kunden zugewiesen ist (z. B. beim Daten-Briefing).</small>
        </label>
      </div>

      <div className="ts-row">
        <div className="ts-field">
          <span>Vergütungsmodell</span>
          <div className="ts-seg">
            <button
              type="button"
              className={`ts-seg-btn ${d.payType === 'commission' ? 'ts-seg-active' : ''}`}
              onClick={() => onChange('payType', 'commission')}
            >Provision (%)</button>
            <button
              type="button"
              className={`ts-seg-btn ${d.payType === 'fixed' ? 'ts-seg-active' : ''}`}
              onClick={() => onChange('payType', 'fixed')}
            >Fixbetrag (€)</button>
          </div>
        </div>

        {d.payType === 'fixed' ? (
          <label className="ts-field">
            <span>Fix pro Kunde / Monat</span>
            <div className="ts-input-suffix">
              <input type="text" inputMode="numeric" value={d.fixedAmount} placeholder="0" onChange={(e) => onChange('fixedAmount', e.target.value)} />
              <span>€</span>
            </div>
          </label>
        ) : (
          <label className="ts-field">
            <span>Provision pro Kunde</span>
            <div className="ts-input-suffix">
              <input type="text" inputMode="numeric" value={d.commissionPercent} placeholder="0" onChange={(e) => onChange('commissionPercent', e.target.value)} />
              <span>%</span>
            </div>
          </label>
        )}

        <label className="ts-field">
          <span>Laufzeit</span>
          <div className="ts-input-suffix">
            <input type="text" inputMode="numeric" value={d.durationMonths} placeholder="0 = laufend" onChange={(e) => onChange('durationMonths', e.target.value)} />
            <span>Monate</span>
          </div>
        </label>

        <label className="ts-field">
          <span>Bonus pro Extra-Termin</span>
          <div className="ts-input-suffix">
            <input type="text" inputMode="numeric" value={d.appointmentBonus} placeholder="0" onChange={(e) => onChange('appointmentBonus', e.target.value)} />
            <span>€</span>
          </div>
        </label>
      </div>

      {/* Provisionsstaffel – greift nur bei Kunden mit aktivem Provisionsmodell. */}
      <div className="ts-row ts-row-plan">
        <label className="ts-field">
          <span>Provisionsstaffel</span>
          <select className="ts-select" value={d.commissionRole} onChange={(e) => onChange('commissionRole', e.target.value)}>
            {COMMISSION_ROLE_OPTIONS.map((o) => (
              <option key={o.value || 'auto'} value={o.value}>{o.label}</option>
            ))}
          </select>
          <small className="ts-field-hint">
            {(() => {
              const roleId = draftCommissionRole(d);
              if (!roleId) return 'Keine gestaffelte Termin-Provision – es gilt der Bonus pro Extra-Termin.';
              return `Gestaffelte Provision nach ${COMMISSION_ROLES[roleId].label}-Plan, sobald der Kunde auf Provisionsmodell steht.`;
            })()}
          </small>
        </label>

        <label className="ts-field">
          <span>Minimum pro Abrechnung</span>
          <div className="ts-input-suffix">
            <input
              type="text"
              inputMode="numeric"
              value={d.commissionMin}
              placeholder={(() => {
                const roleId = draftCommissionRole(d);
                return roleId ? String(COMMISSION_ROLES[roleId].defaultMinimum) : '0';
              })()}
              onChange={(e) => onChange('commissionMin', e.target.value)}
              disabled={!draftCommissionRole(d)}
            />
            <span>€</span>
          </div>
          <small className="ts-field-hint">Garantierte Untergrenze. Leer = Standard des Vergütungsplans.</small>
        </label>
      </div>
    </>
  );

  return (
    <div className="ts-wrap">
      <motion.div className="ts-inner" variants={staggerContainer} initial="initial" animate="animate">
        <motion.header className="ts-head" variants={itemVariants}>
          <div>
            <h1>Team &amp; Provisionen</h1>
            <p>Lege fest, wer im Team ist und was jede:r pro Kunde bekommt – ein monatlicher Fixbetrag oder eine Provision, und wie lange sie läuft.</p>
          </div>
        </motion.header>

        {/* Neuen Mitarbeiter anlegen */}
        <motion.section className="ts-card ts-card-new" variants={itemVariants}>
          <div className="ts-card-title">Mitarbeiter hinzufügen</div>
          {renderFields(newDraft, (field, value) => setNewDraft((p) => ({ ...p, [field]: value })))}
          <div className="ts-card-foot">
            {renderPayoutPreview(newDraft)}
            <button type="button" className="ts-btn ts-btn-primary" onClick={addMember} disabled={!newDraft.name.trim()}>
              + Hinzufügen &amp; speichern
            </button>
          </div>
        </motion.section>

        {/* Bestehende Mitarbeiter */}
        <motion.div className="ts-list-head" variants={itemVariants}>Team ({ids.length})</motion.div>
        {loading ? (
          <div className="ts-empty">Laden…</div>
        ) : ids.length === 0 ? (
          <div className="ts-empty">Noch keine Mitarbeiter. Lege oben den ersten an.</div>
        ) : (
          ids.map((id) => {
            const d = drafts[id] || toDraft(members[id]);
            const dirty = isDirty(id);
            return (
              <motion.section key={id} variants={itemVariants} className={`ts-card ${d.active === false ? 'ts-card-inactive' : ''}`}>
                <div className="ts-card-top">
                  <span className={`ts-badge ${d.payType === 'fixed' ? 'ts-badge-fix' : 'ts-badge-com'}`}>
                    {d.payType === 'fixed' ? 'Fixbetrag' : 'Provision'}
                  </span>
                  <label className="ts-toggle">
                    <input type="checkbox" checked={d.active !== false} onChange={(e) => setField(id, 'active', e.target.checked)} />
                    <span>{d.active !== false ? 'aktiv' : 'inaktiv'}</span>
                  </label>
                </div>
                {renderFields(d, (field, value) => setField(id, field, value))}
                {renderShare(id)}
                <div className="ts-card-foot">
                  {renderPayoutPreview(d)}
                  <div className="ts-card-actions">
                    {dirty && <button type="button" className="ts-btn ts-btn-ghost" onClick={() => resetMember(id)}>Verwerfen</button>}
                    <button type="button" className="ts-btn ts-btn-del" onClick={() => deleteMember(id)}>Löschen</button>
                    <button type="button" className="ts-btn ts-btn-primary" onClick={() => saveMember(id)} disabled={!dirty || savingId === id}>
                      {savingId === id ? 'Speichert…' : dirty ? 'Speichern' : 'Gespeichert'}
                    </button>
                  </div>
                </div>
              </motion.section>
            );
          })
        )}
      </motion.div>
    </div>
  );
};

export default TeamSettings;
